import { createSlice } from "@reduxjs/toolkit";
import {
  getInvoices,
  getInvoice,
  getInvoicePdf,
  addNewInvoice,
  updateInvoice,
  deleteInvoice,
  setInvoiceAsPaid,
  cancelInvoice,
  getOverdueInvoices
} from "./thunk";

const initialState = {
  invoices: [],
  overdueData: {},
  invoicePdf: {
    data: null,
    loading: false,
    error: null,
    invoiceId: null
  },
  currentInvoice: null,
  loading: false,
  error: null,
  filters: {
    status: null,
    client: null,
    contract: null,
  },
  pagination: {
    page: 1,
    totalPages: 1,
    totalCount: 0,
  }
};

const invoiceSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        client: null,
        contract: null,
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    resetInvoices: (state) => {
      state.invoices = [];
      state.currentInvoice = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get All Invoices
      .addCase(getInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload;
        // If pagination data is included in response
        if (action.payload.results) {
          state.invoices = action.payload.results;
          state.pagination.totalCount = action.payload.count;
          state.pagination.totalPages = Math.ceil(action.payload.count / 10);
        }
      })
      .addCase(getInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get All overdue invoice
      .addCase(getOverdueInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOverdueInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.overdueData = action.payload;
        state.error = null;
      })
      .addCase(getOverdueInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Invoice By ID
      .addCase(getInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInvoice = action.payload;
      })
      .addCase(getInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Invoice PDF
     .addCase(getInvoicePdf.pending, (state, action) => {
        state.invoicePdf.loading = true;
        state.invoicePdf.error = null;
        state.invoicePdf.invoiceId = action.meta.arg;
      })
      .addCase(getInvoicePdf.fulfilled, (state, action) => {
        if (state.invoicePdf.data) {
          window.URL.revokeObjectURL(state.invoicePdf.data);
        }
        
        state.invoicePdf.loading = false;
        state.invoicePdf.data = action.payload.pdfUrl;
        state.invoicePdf.error = null;
      })
      .addCase(getInvoicePdf.rejected, (state, action) => {
        state.invoicePdf.loading = false;
        state.invoicePdf.error = action.payload;
        state.invoicePdf.data = null;
      })

      // Add New Invoice
      .addCase(addNewInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices.unshift(action.payload); // Add to beginning of list
        state.pagination.totalCount += 1;
      })
      .addCase(addNewInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Invoice
      .addCase(updateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update in invoices list
        const index = state.invoices.findIndex(
          invoice => invoice.id === action.payload.id
        );
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }

        // Update current invoice if it's the same one
        if (state.currentInvoice && state.currentInvoice.id === action.payload.id) {
          state.currentInvoice = action.payload;
        }
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Invoice
      .addCase(deleteInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.loading = false;
        
        // Remove from invoices list
        state.invoices = state.invoices.filter(
          invoice => invoice.id !== action.payload
        );
        
        // Clear current invoice if it was deleted
        if (state.currentInvoice && state.currentInvoice.id === action.payload) {
          state.currentInvoice = null;
        }
        
        state.pagination.totalCount -= 1;
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Set Invoice as Paid
      .addCase(setInvoiceAsPaid.pending, (state) => {
        state.error = null;
      })
      .addCase(setInvoiceAsPaid.fulfilled, (state, action) => {
        // Update the specific invoice in the list
        const invoiceIndex = state.invoices.findIndex(
          invoice => invoice.id === action.meta.arg
        );
        
        if (invoiceIndex !== -1) {
          state.invoices[invoiceIndex] = {
            ...state.invoices[invoiceIndex],
            paid: true,
            status: 'paid',
            paid_at: new Date().toISOString()
          };
        }

        // Update current invoice if it's the same one
        if (state.currentInvoice && state.currentInvoice.id === action.meta.arg) {
          state.currentInvoice = {
            ...state.currentInvoice,
            paid: true,
            status: 'paid',
            paid_at: new Date().toISOString()
          };
        }
      })
      .addCase(setInvoiceAsPaid.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Set Invoice as canceled
      .addCase(cancelInvoice.pending, (state) => {
        state.error = null;
      })
      .addCase(cancelInvoice.fulfilled, (state, action) => {
        const invoiceIndex = state.invoices.findIndex(
          invoice => invoice.id === action.meta.arg
        );
        
        if (invoiceIndex !== -1) {
          state.invoices[invoiceIndex] = {
            ...state.invoices[invoiceIndex],
            canceled: true,
            status: 'canceled'
          };
        }

        if (state.currentInvoice && state.currentInvoice.id === action.meta.arg) {
          state.currentInvoice = {
            ...state.invoices[invoiceIndex],
            canceled: true,
            status: 'canceled'
          };
        }
      })
      .addCase(cancelInvoice.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearCurrentInvoice,
  setFilters,
  clearFilters,
  setPagination,
  resetInvoices
} = invoiceSlice.actions;

export default invoiceSlice.reducer;

// Selectors
export const selectInvoices = (state) => state.invoices.invoices;
export const selectCurrentInvoice = (state) => state.invoices.currentInvoice;
export const selectInvoicesLoading = (state) => state.invoices.loading;
export const selectInvoicesError = (state) => state.invoices.error;
export const selectInvoiceFilters = (state) => state.invoices.filters;
export const selectInvoicePagination = (state) => state.invoices.pagination;

// Filtered selectors
export const selectPaidInvoices = (state) => 
  state.invoices.invoices.filter(invoice => invoice.paid === true);

export const selectUnpaidInvoices = (state) => 
  state.invoices.invoices.filter(invoice => invoice.paid === false);

export const selectInvoicesByStatus = (status) => (state) =>
  state.invoices.invoices.filter(invoice => invoice.status === status);

export const selectDraftInvoices = (state) => 
  state.invoices.invoices.filter(invoice => invoice.status === 'draft');

export const selectSentInvoices = (state) => 
  state.invoices.invoices.filter(invoice => invoice.status === 'sent');

export const selectOverdueInvoices = (state) => 
  state.invoices.invoices.filter(invoice => invoice.status === 'overdue');